import { Component, OnInit, OnDestroy, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CoreConfigService } from "@core/services/config.service";

export interface ShortcutItem {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  color?: string;
  link: string;
}

@Component({
  selector: "app-navbar-shortcuts",
  templateUrl: "./navbar-shortcuts.component.html",
  styleUrls: ["./navbar-shortcuts.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarShortcutsComponent implements OnInit, OnDestroy {
  readonly STORAGE_KEY = "vuexy_user_shortcuts";
  public showShortcuts = false;
  private destroy$ = new Subject<void>();

  defaultShortcuts: ShortcutItem[] = [
    {
      id: "dashboard",
      title: "Bảng điều khiển",
      subtitle: "Tổng quan SmartCity",
      icon: "layout-dashboard",
      color: "primary",
      link: "/smartcity/dashboard",
    },
    {
      id: "camera",
      title: "Giám sát Camera",
      subtitle: "Xem trực tiếp 24/7",
      icon: "video",
      color: "success",
      link: "/smartcity/camera",
    },
    {
      id: "report",
      title: "Báo cáo tổng hợp",
      subtitle: "Thống kê sự kiện AI",
      icon: "chart-pie",
      color: "primary",
      link: "/smartcity/ai-report",
    },
    {
      id: "face-events",
      title: "Nhận diện khuôn mặt",
      subtitle: "Lịch sử nhận diện AI",
      icon: "users",
      color: "primary",
      link: "/smartcity/face-events",
    },
    {
      id: "watchlist",
      title: "Danh sách theo dõi",
      subtitle: "Quản lý đối tượng",
      icon: "shield-alert",
      color: "dark",
      link: "/smartcity/watchlist",
    },
    {
      id: "infra-ops",
      title: "Hạ tầng vận hành",
      subtitle: "Giám sát hệ thống",
      icon: "server",
      color: "info",
      link: "/smartcity/infra-ops",
    },
  ];

  allAvailableRoutes: ShortcutItem[] = [
    ...this.defaultShortcuts,
    {
      id: "traffic-map",
      title: "Bản đồ điện tử GIS",
      subtitle: "Bản đồ vị trí & camera",
      icon: "map-pin",
      color: "info",
      link: "/smartcity/traffic-map",
    },
    {
      id: "lpr-log",
      title: "Sự kiện phương tiện",
      subtitle: "Biển số & Giao thông",
      icon: "car",
      color: "warning",
      link: "/smartcity/lpr-log",
    },
    {
      id: "behavior-events",
      title: "Sự kiện hành vi",
      subtitle: "Cảnh báo xâm nhập & ngã",
      icon: "activity",
      color: "warning",
      link: "/smartcity/behavior-events",
    },
    {
      id: "fire-events",
      title: "Sự kiện cháy nổ",
      subtitle: "Báo động khói & lửa PCCC",
      icon: "flame",
      color: "danger",
      link: "/smartcity/fire-events",
    },
    {
      id: "ai-search",
      title: "Tìm kiếm thông minh",
      subtitle: "Truy vấn hình ảnh AI",
      icon: "search",
      color: "info",
      link: "/smartcity/ai-search",
    },
    {
      id: "alert-rules",
      title: "Cấu hình thông báo",
      subtitle: "Quy tắc cảnh báo SMS/App",
      icon: "bell-ring",
      color: "danger",
      link: "/smartcity/alert-rules",
    },
    {
      id: "devices",
      title: "Cấu hình thiết bị",
      subtitle: "Vận hành phần cứng",
      icon: "cpu",
      color: "secondary",
      link: "/smartcity/devices",
    },
    {
      id: "recording-config",
      title: "Cấu hình lưu trữ",
      subtitle: "Ghi hình & Playback",
      icon: "settings",
      color: "secondary",
      link: "/smartcity/recording-config",
    },
    {
      id: "ui-components",
      title: "UI Components Showcase",
      subtitle: "Thư viện giao diện dùng chung",
      icon: "layout-grid",
      color: "primary",
      link: "/smartcity/ui-components",
    },
  ];

  shortcuts: ShortcutItem[] = [];
  isManageOpen = false;

  constructor(
    private router: Router,
    private _coreConfigService: CoreConfigService
  ) {}

  ngOnInit(): void {
    this.loadShortcuts();
    this._coreConfigService.config
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        // High-level config check like StatsInfoComponent + fallback for URL & port
        const isAppSmartCity = config?.app?.appName === "SmartCity";
        this.showShortcuts = isAppSmartCity || this.isSmartCityRoute;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isSmartCityRoute(): boolean {
    const url = this.router?.url || "";
    const port = typeof window !== "undefined" ? window.location?.port : "";
    return (
      port === "4203" ||
      url.includes("/smartcity") ||
      url.includes("camera") ||
      url.includes("dashboard") ||
      url.includes("face-events") ||
      url.includes("behavior-events") ||
      url.includes("fire-events") ||
      url.includes("ai-report") ||
      url.includes("traffic-map") ||
      url.includes("lpr-log") ||
      url.includes("watchlist") ||
      url.includes("alert-rules") ||
      url.includes("infra-ops") ||
      url.includes("devices") ||
      url.includes("recording-config") ||
      url.includes("ai-search") ||
      url.includes("ui-components")
    );
  }

  loadShortcuts(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const parsed: ShortcutItem[] = JSON.parse(saved);
        this.shortcuts = parsed.map((item) => {
          const match = this.allAvailableRoutes.find((r) => r.id === item.id);
          return match
            ? {
                ...item,
                icon: match.icon,
                color: match.color,
                title: match.title,
                subtitle: match.subtitle,
                link: match.link,
              }
            : item;
        });
        this.saveShortcuts();
      } else {
        this.shortcuts = [...this.defaultShortcuts];
        this.saveShortcuts();
      }
    } catch (e) {
      this.shortcuts = [...this.defaultShortcuts];
    }
  }

  saveShortcuts(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.shortcuts));
    } catch (e) {
      console.warn("Không thể lưu shortcut vào localStorage", e);
    }
  }

  isShortcutActive(routeId: string): boolean {
    return this.shortcuts.some((s) => s.id === routeId);
  }

  toggleShortcut(item: ShortcutItem): void {
    if (this.isShortcutActive(item.id)) {
      this.shortcuts = this.shortcuts.filter((s) => s.id !== item.id);
    } else {
      this.shortcuts.push(item);
    }
    this.saveShortcuts();
  }

  resetDefaults(): void {
    this.shortcuts = [...this.defaultShortcuts];
    this.saveShortcuts();
  }

  onAddShortcut(): void {
    this.isManageOpen = !this.isManageOpen;
  }
}
